import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiArrowLeft, FiTrash2, FiUser, FiSave,
  FiCalendar, FiMapPin, FiDollarSign, FiCreditCard, FiCheck,
  FiEdit2, FiX, FiDownload,
} from 'react-icons/fi';
import { generateSalesOrderPDF, downloadSalesOrderPDF } from '../utils/generateSalesOrderPDF';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';
import AddUserModal from '../components/admin/AddUserModal';
import AddBusinessModal from '../components/admin/AddBusinessModal';
import ProductPicker from '../components/admin/ProductPicker';
import { isPaymentTermMet } from '../components/OrderTrackingIndicator';
import './SalesOrders.css';

/* ────────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────────── */

const UNITS = ['Nos', 'Sqft', 'Kg', 'Bundle', 'Pcs', 'Box', 'Roll', 'Set', 'Ft', 'Meter'];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({
  id: `L${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
  product: '', description: '', hsn: '', sku: '', catalogProductId: null,
  qty: 1, unit: 'Nos', price: 0, taxPercent: 18,
});

const emptyCustomer = () => ({
  businessId: null, userId: null,
  customerName: '', customerPhone: '', customerEmail: '',
  companyName: '', gstNo: '', companyAddress: '',
});

const emptyAddress = () => ({
  line1: '', line2: '', city: '', state: '', pincode: '', country: 'India',
});

const lineTotal = (l) => {
  const base = (Number(l.qty) || 0) * (Number(l.price) || 0);
  return base + base * ((Number(l.taxPercent) || 0) / 100);
};

const computeTotals = (lines) => {
  let taxableAmount = 0, taxAmount = 0;
  lines.forEach((l) => {
    const base = (Number(l.qty) || 0) * (Number(l.price) || 0);
    taxableAmount += base;
    taxAmount += base * ((Number(l.taxPercent) || 0) / 100);
  });
  return { taxableAmount, taxAmount, grandTotal: taxableAmount + taxAmount };
};

const STATUS_META = {
  draft:     { label: 'Draft',     tone: 'gray'  },
  confirmed: { label: 'Confirmed', tone: 'green' },
  cancelled: { label: 'Cancelled', tone: 'red'   },
};

/* Derive current tracking stage label + tone for a list-row badge */
const getTrackingBadge = (order, activeTerm) => {
  if (!order) return { label: 'Draft', tone: 'gray' };
  const status = order.status || 'draft';
  if (status === 'draft')     return { label: 'Draft',     tone: 'gray' };
  if (status === 'cancelled') return { label: 'Cancelled', tone: 'red'  };

  const readyEntries = Array.isArray(order.readyEntries) ? order.readyEntries : [];
  const paymentMet   = isPaymentTermMet(order, activeTerm);

  if (status === 'delivered')       return { label: 'Delivered',  tone: 'blue'   };
  if (readyEntries.length > 0)      return { label: 'Ready',      tone: 'purple' };
  if (paymentMet)                   return { label: 'Production', tone: 'orange' };
  return { label: 'Confirmed', tone: 'green' };
};



/* ────────────────────────────────────────────────────────────────────
   Parse deliveryAddress from DB (stored as JSON string in TEXT column)
   ──────────────────────────────────────────────────────────────────── */
const parseDeliveryAddress = (raw) => {
  if (!raw) return emptyAddress();
  if (typeof raw === 'object') return { ...emptyAddress(), ...raw };
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return { ...emptyAddress(), ...parsed };
    // old plain-text fallback
    return { ...emptyAddress(), line1: String(parsed) };
  } catch {
    return { ...emptyAddress(), line1: raw };
  }
};

/* ────────────────────────────────────────────────────────────────────
   Order List Page
   ──────────────────────────────────────────────────────────────────── */

const PAGE_SIZE = 25;

let savedSearch = '';

const OrderListPage = () => {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState(savedSearch);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const [activeTerm, setActiveTerm]     = useState(null);
  const navigate = useNavigate();

  // Fetch active payment term once — needed for Production step in indicator
  useEffect(() => {
    API.get('/payment-terms/active')
      .then(({ data }) => setActiveTerm(data.term || null))
      .catch(() => {});
  }, []);

  const load = useCallback(async ({
    targetPage  = 1,
    q           = search,
    status      = filterStatus,
    payment     = filterPayment,
    from        = dateFrom,
    to          = dateTo,
  } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim())          params.set('q', q.trim());
      if (status !== 'all')  params.set('status', status);
      if (payment !== 'all') params.set('paymentType', payment);
      if (from)              params.set('orderFrom', from);
      if (to)                params.set('orderTo', to);
      params.set('page', targetPage);
      params.set('limit', PAGE_SIZE);
      const { data } = await API.get(`/sales-orders?${params}`);
      setOrders(data.orders || []);
      setTotalPages(data.pages || 1);
      setTotalCount(data.count || 0);
      setPage(targetPage);
    } catch {
      toast.error('Could not load orders');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterPayment, dateFrom, dateTo]);

  // Initial load
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync search to module variable so back navigation restores it
  useEffect(() => {
    savedSearch = search;
  }, [search]);

  // Search — debounced, reset to page 1
  useEffect(() => {
    const t = setTimeout(() => load({ targetPage: 1, q: search }), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Status / payment filter — immediate, reset to page 1
  useEffect(() => {
    load({ targetPage: 1, status: filterStatus, payment: filterPayment });
  }, [filterStatus, filterPayment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date filter — immediate, reset to page 1
  useEffect(() => {
    load({ targetPage: 1, from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilter = filterStatus !== 'all' || filterPayment !== 'all' || dateFrom || dateTo;

  return (
    <div className="so-page">
      <div className="so-page-head">
        <div>
          <h2>Orders</h2>
        </div>
        <button className="so-btn so-btn--primary" onClick={() => navigate('/admin/sales-orders/new')}>
          <FiPlus size={16} /> New Order
        </button>
      </div>

      {/* ── Filter bar ── */}
      <div className="so-filter-bar">
        {/* Search */}
        <div className="so-search-input so-search-input--filter">
          <FiSearch size={15} />
          <input
            placeholder="Search order no., customer, company or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Date range */}
        <div className="so-filter-dates">
          <input
            type="date"
            className="so-date-input"
            title="From date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
          <span className="so-date-sep">–</span>
          <input
            type="date"
            className="so-date-input"
            title="To date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>

        {/* Status pills */}
        <div className="so-pill-group">
          {[['all','All'],['draft','Draft'],['confirmed','Confirmed'],['cancelled','Cancelled']].map(([val, label]) => (
            <button
              key={val}
              className={`so-pill${filterStatus === val ? ' so-pill--active' : ''}`}
              onClick={() => setFilterStatus(val)}
            >{label}</button>
          ))}
        </div>

        {/* Payment pills */}
        <div className="so-pill-group">
          {[['all','All'],['cash','Cash'],['credit','Credit']].map(([val, label]) => (
            <button
              key={val}
              className={`so-pill${filterPayment === val ? ' so-pill--active' : ''}`}
              onClick={() => setFilterPayment(val)}
            >{label}</button>
          ))}
        </div>

        {hasFilter && (
          <button className="so-clear-btn" onClick={() => {
            setFilterStatus('all'); setFilterPayment('all');
            setDateFrom(''); setDateTo('');
          }}>Clear</button>
        )}
      </div>

      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="so-empty">{search || hasFilter ? 'No matching orders.' : 'No orders yet — create your first one.'}</div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Order Date</th>
                  <th>Payment</th>
                  <th>Tracking</th>
                  <th style={{ textAlign: 'right' }}>Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} onClick={() => navigate(`/admin/sales-orders/${o.id}`)}>
                    <td className="so-strong">{o.orderNumber}</td>
                    <td>
                      <div className="so-strong">{o.companyName || o.customerName}</div>
                      <div className="so-muted">{o.companyName ? o.customerName : o.customerPhone}</div>
                    </td>
                    <td className="so-muted">{fmtDate(o.orderDate)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{o.paymentType || 'cash'}</td>
                    <td>
                      {(() => {
                        const { label, tone } = getTrackingBadge(o, activeTerm);
                        return <span className={`so-badge so-badge--${tone}`}>{label}</span>;
                      })()}
                    </td>
                    <td style={{ textAlign: 'right' }} className="so-strong">{money(o.grandTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Page {page} of {totalPages} &nbsp;·&nbsp; {totalCount} orders
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page - 1 })}
              disabled={page <= 1}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page <= 1 ? 0.4 : 1 }}
            >
              ‹ Prev
            </button>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page + 1 })}
              disabled={page >= totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page >= totalPages ? 0.4 : 1 }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Customer search + inline create block
   ──────────────────────────────────────────────────────────────────── */

const CustomerBlock = ({ customer, setCustomer, locked, onCreditDetected }) => {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const { data } = await API.get(`/sales-orders/lookup?q=${encodeURIComponent(query.trim())}`);
        setMatches(data.matches || []);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const applyMatch = async (m) => {
    setCustomer({
      businessId: m.businessId || null,
      userId: m.userId || null,
      customerName: m.customerName || '',
      customerPhone: m.customerPhone || '',
      customerEmail: m.customerEmail || '',
      companyName: m.companyName || '',
      gstNo: m.gstNo || '',
      companyAddress: m.companyAddress || '',
    });
    setOpen(false);
    toast.success('Customer details loaded');

    // If business has credit, notify parent to auto-select credit payment
    if (m.businessId && onCreditDetected) {
  try {
    const [creditRes, billingRes] = await Promise.all([
      API.get(`/sales-orders/credit-summary/${m.businessId}`).catch(() => null),
      API.get(`/monthly-billing/${m.businessId}`).catch(() => null),
    ]);

    const isMonthlyBilling = billingRes?.data?.billing?.monthlyBillingEnabled;
    const creditData = creditRes?.data;

    if (isMonthlyBilling) {
      // Monthly billing takes priority — always credit, due date = end of month + due days
      onCreditDetected(null, true, {
        paymentDueDays: billingRes.data.billing.paymentDueDays,
      });
    } else if (creditData?.creditLimit > 0) {
      // Business has a credit limit assigned
      onCreditDetected({
        creditLimit: creditData.creditLimit,
        availableCredit: creditData.availableCredit,
        usedCredit: creditData.usedCredit,
        creditTenureDays: creditData.creditTenureDays,
      });
    } else {
      // No credit, no monthly billing
      onCreditDetected(null);
    }
  } catch {
    onCreditDetected(null);
  }
}
  };

  // New user created via the shared "Add New User" popup (individual customer)
  const handleUserCreated = (user) => {
    setCustomer({
      businessId: null,
      userId: user.id,
      customerName: user.name || '',
      customerPhone: user.phone || '',
      customerEmail: user.email || '',
      companyName: '',
      gstNo: '',
      companyAddress: user.address || '',
    });
    setShowAddUser(false);
    setOpen(false);
  };

  // New business created via the shared "Add New Business" popup
  const handleBusinessCreated = (business) => {
    const address = [
      business.regAddressLine1, business.regAddressLine2,
      business.regCity, business.regState, business.regPincode,
    ].filter(Boolean).join(', ');
    setCustomer({
      businessId: business.id,
      userId: business.userId || null,
      customerName: business.contactPersonName || '',
      customerPhone: business.contactPersonPhone || '',
      customerEmail: business.contactPersonEmail || '',
      companyName: business.companyName || '',
      gstNo: business.gstNumber || '',
      companyAddress: address,
    });
    setShowAddBusiness(false);
    setOpen(false);
  };

  const clearCustomer = () => {
    setCustomer(emptyCustomer());
    setQuery('');
  };

  const hasCustomer = !!customer.customerName;

  if (hasCustomer) {
    return (
      <div className="so-customer-card">
        <div className="so-customer-card-head">
          <span className="so-field-label">Customer</span>
          {!locked && (
            <button className="so-link-btn" onClick={clearCustomer}><FiSearch size={12} /> Change</button>
          )}
        </div>
        <div className="so-customer-name">{customer.companyName || customer.customerName}</div>
        {customer.companyName && <div className="so-customer-sub">Contact Person: {customer.customerName}</div>}
        {customer.companyAddress && <div className="so-customer-addr">{customer.companyAddress}</div>}
        <div className="so-customer-sub">{customer.customerPhone}{customer.customerEmail ? ` · ${customer.customerEmail}` : ''}</div>
        {customer.gstNo && <div className="so-customer-sub">GSTIN: {customer.gstNo}</div>}
      </div>
    );
  }

  return (
    <div className="so-customer-block" ref={wrapRef}>
      <label className="so-field-label">Customer</label>
      <div className="so-search-input">
        <FiSearch size={16} />
        <input
          placeholder="Search a name, company or Tax ID…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {searching && <span className="so-spinner" />}
      </div>
      {open && (matches.length > 0 || query.trim().length >= 2) && (
        <div className="so-match-list">
          {matches.map((m, i) => (
            <button key={i} className="so-match-item" onClick={() => applyMatch(m)}>
              <FiUser size={14} />
              <div>
                <div className="so-strong">{m.companyName || m.customerName}</div>
                <div className="so-muted">{m.companyName ? `${m.customerName} · ` : ''}{m.customerPhone}</div>
              </div>
            </button>
          ))}
          <div className="so-match-create-row">
            <button className="so-match-item so-match-item--create" onClick={() => { setShowAddUser(true); setOpen(false); }}>
              <FiPlus size={14} />
              <div>
                <div className="so-strong">Create new user</div>
                <div className="so-muted">No match — add an individual customer</div>
              </div>
            </button>
            <button className="so-match-item so-match-item--create" onClick={() => { setShowAddBusiness(true); setOpen(false); }}>
              <FiPlus size={14} />
              <div>
                <div className="so-strong">Create new business</div>
                <div className="so-muted">No match — add a business customer</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {showAddUser && (
        <AddUserModal onClose={() => setShowAddUser(false)} onCreated={handleUserCreated} />
      )}
      {showAddBusiness && (
        <AddBusinessModal onClose={() => setShowAddBusiness(false)} onCreated={handleBusinessCreated} />
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Product line table
   ──────────────────────────────────────────────────────────────────── */

const LineTable = ({ lines, setLines, readOnly = false }) => {
  const rowRefs = useRef({});
  const productInputRefs = useRef({});

  const update = (id, field, value) => setLines(ls => ls.map(l => l.id === id ? { ...l, [field]: value } : l));
  const remove = (id) => setLines(ls => ls.filter(l => l.id !== id));

  // Catalog product chosen (or just quick-added as draft) -> fill name, SKU, HSN, GST %.
  // Price / qty / unit / description are left for the admin. All filled values stay editable
  // and only change this order line, never the catalog product.
  const pickProduct = (id, p) => setLines(ls => ls.map(l => l.id === id ? {
    ...l,
    product: p.name,
    sku: p.sku || '',
    hsn: p.hsnCode || '',
    catalogProductId: p.id,
    taxPercent: p.gstPercent !== null && p.gstPercent !== undefined ? p.gstPercent : l.taxPercent,
  } : l));

  // Auto-resize all textareas whenever lines change (handles initial load + external updates)
  useEffect(() => {
    if (readOnly) return;
    const allTextareas = document.querySelectorAll('.so-line-product, .so-line-desc');
    allTextareas.forEach(ta => {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    });
  }, [lines, readOnly]);

  const addLine = () => {
    const newLine = emptyLine();
    setLines(ls => [...ls, newLine]);
    return newLine.id;
  };

  // A line counts as "untouched" if the admin never typed anything into it —
  // qty/tax keep their defaults, product/description/price are still blank/zero.
  const isLineEmpty = (l) =>
    !l.product.trim() && !l.description.trim() && Number(l.price) === 0 && Number(l.qty) === 1;

  // Tab out of the last line's Tax % field -> auto-add a new line and focus its Product input.
  const handleTaxKeyDown = (idx, e) => {
  if (e.key === 'Tab' && !e.shiftKey && idx === lines.length - 1) {
    const currentLine = lines[idx];
    if (isLineEmpty(currentLine)) {
      // empty line — delete it and let Tab fall through to Notes naturally
      e.preventDefault();
      if (lines.length > 1) {
        setLines(ls => ls.filter(l => l.id !== currentLine.id));
      }
      // focus the textarea (Notes) directly
      setTimeout(() => {
        const textarea = document.querySelector('.so-notes-col textarea');
        if (textarea) textarea.focus();
      }, 0);
    } else {
      // line has content — add a new line as before
      e.preventDefault();
      const newId = addLine();
      setTimeout(() => { productInputRefs.current[newId]?.focus(); }, 0);
    }
  }
};

  // Focus left the row entirely (not just moved between its own inputs) —
  // if it's still empty, delete it. Always keep at least one line.
  const handleRowBlur = (id, e) => {
    const rowEl = rowRefs.current[id];
    if (rowEl && rowEl.contains(e.relatedTarget)) return;
    setLines(ls => {
      const line = ls.find(x => x.id === id);
      if (line && isLineEmpty(line) && ls.length > 1) {
        return ls.filter(x => x.id !== id);
      }
      return ls;
    });
  };

  if (readOnly) {
    return (
      <div className="so-lines">
        <table className="so-lines-table">
          <thead>
            <tr>
              <th style={{ width: '34%' }}>Product</th>
              <th style={{ width: '10%' }}>Quantity</th>
              <th style={{ width: '10%' }}>Unit</th>
              <th style={{ width: '12%' }}>Price</th>
              <th style={{ width: '10%' }}>Tax %</th>
              <th style={{ width: '16%', textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>
                  <div className="so-line-product-view">{l.product || '—'}</div>
                  {l.description && <div className="so-line-desc-view">{l.description}</div>}
                  {(l.hsn || l.sku) && (
                    <div className="so-line-meta-view">
                      {[l.hsn && `HSN ${l.hsn}`, l.sku && `SKU ${l.sku}`].filter(Boolean).join('  ·  ')}
                    </div>
                  )}
                </td>
                <td>{l.qty}</td>
                <td>{l.unit}</td>
                <td>{money(l.price)}</td>
                <td>{l.taxPercent}%</td>
                <td style={{ textAlign: 'right' }} className="so-strong">{money(lineTotal(l))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="so-lines">
      <table className="so-lines-table">
        <thead>
          <tr>
            <th style={{ width: '34%' }}>Product</th>
            <th style={{ width: '10%' }}>Quantity</th>
            <th style={{ width: '10%' }}>Unit</th>
            <th style={{ width: '12%' }}>Price</th>
            <th style={{ width: '10%' }}>Tax %</th>
            <th style={{ width: '16%', textAlign: 'right' }}>Amount</th>
            <th style={{ width: '4%' }} />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, idx) => (
            <tr
              key={l.id}
              ref={(el) => { rowRefs.current[l.id] = el; }}
              onBlur={(e) => handleRowBlur(l.id, e)}
            >
              <td>
                <ProductPicker
                  inputRef={(el) => { productInputRefs.current[l.id] = el; }}
                  value={l.product}
                  onTextChange={(text) =>
                    // Any edit un-links the line from the catalog product.
                    // Erasing the name completely also erases the SKU / HSN that came with it.
                    setLines(ls => ls.map(x => x.id !== l.id ? x : (
                      text.trim()
                        ? { ...x, product: text, catalogProductId: null }
                        : { ...x, product: text, catalogProductId: null, sku: '', hsn: '' }
                    )))
                  }
                  onPick={(p) => pickProduct(l.id, p)}
                />
                <textarea
                  className="so-line-desc"
                  placeholder="Description (optional)"
                  value={l.description}
                  rows={1}
                  onChange={(e) => {
                    update(l.id, 'description', e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
                <div className="so-line-meta">
                  <input
                    placeholder="HSN"
                    value={l.hsn || ''}
                    onChange={(e) => update(l.id, 'hsn', e.target.value)}
                  />
                  <input
                    placeholder="SKU"
                    value={l.sku || ''}
                    onChange={(e) => update(l.id, 'sku', e.target.value)}
                  />
                </div>
              </td>
              <td><input type="number" min="0" step="any" value={l.qty} onChange={(e) => update(l.id, 'qty', e.target.value)} /></td>
              <td>
                <select value={l.unit} onChange={(e) => update(l.id, 'unit', e.target.value)}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </td>
              <td><input type="number" min="0" step="any" value={l.price} onChange={(e) => update(l.id, 'price', e.target.value)} /></td>
              <td>
                <input
                  type="number" min="0" max="100" step="any"
                  value={l.taxPercent}
                  onChange={(e) => update(l.id, 'taxPercent', e.target.value)}
                  onKeyDown={(e) => handleTaxKeyDown(idx, e)}
                />
              </td>
              <td style={{ textAlign: 'right' }} className="so-strong">{money(lineTotal(l))}</td>
              <td>
                {lines.length > 1 && (
                  <button className="so-icon-btn" onClick={() => remove(l.id)}><FiTrash2 size={14} /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="so-link-btn so-add-line" onClick={addLine}><FiPlus size={14} /> Add a line</button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Delivery Address Popup
   ──────────────────────────────────────────────────────────────────── */

const ADDR_FIELDS = [
  { key: 'line1',   label: 'Address Line 1 *',  placeholder: 'Street / Building / Flat no.' },
  { key: 'line2',   label: 'Address Line 2',     placeholder: 'Area / Landmark (optional)'   },
  { key: 'city',    label: 'City',               placeholder: ''                              },
  { key: 'state',   label: 'State',              placeholder: ''                              },
  { key: 'pincode', label: 'Pin Code',           placeholder: ''                              },
  { key: 'country', label: 'Country',            placeholder: ''                              },
];

const DeliveryAddressPopup = ({ initial, initialSameAsCompany, companyAddress, onSave, onClose }) => {
  const [sameAsCompany, setSameAsCompany] = useState(!!initialSameAsCompany);
  const [form, setForm] = useState({ ...emptyAddress(), ...initial });

  // Tick "Same as company address" -> auto-fill from the customer's company address.
  // Untick -> clear back to a blank form so the admin can fill it in manually.
  const handleSameAsCompanyChange = (checked) => {
    setSameAsCompany(checked);
    if (checked) {
      setForm({ ...emptyAddress(), line1: companyAddress || '' });
    } else {
      setForm(emptyAddress());
    }
  };

  const handleSave = () => {
    if (!sameAsCompany && !form.line1.trim()) { toast.error('Address Line 1 is required'); return; }
    if (sameAsCompany && !companyAddress) { toast.error('No company address on file for this customer'); return; }
    onSave({
      sameAsCompany,
      address: sameAsCompany ? { ...emptyAddress(), line1: companyAddress || '' } : { ...form },
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 16, fontWeight: 700 }}>Delivery Address</h3>

        <label className="so-checkbox-row" style={{ marginBottom: 18 }}>
          <input
            type="checkbox"
            checked={sameAsCompany}
            onChange={(e) => handleSameAsCompanyChange(e.target.checked)}
          />
          Same as company address
        </label>

        {sameAsCompany ? (
          <div style={{
            fontSize: 13, color: '#444', background: '#f5f6f8', borderRadius: 6,
            padding: '10px 12px', marginBottom: 4, lineHeight: 1.5,
          }}>
            {companyAddress || 'No company address on file for this customer.'}
          </div>
        ) : (
          ADDR_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                {label}
              </label>
              <input
                type="text"
                value={form[key]}
                onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                  border: '1px solid #ddd', borderRadius: 6, fontSize: 14, outline: 'none',
                }}
              />
            </div>
          ))
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
          >
            Save Address
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Order Form Page (new + edit share this)
   ──────────────────────────────────────────────────────────────────── */

const OrderFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { log } = useStaffLog();

  // Edit-mode state: new orders start in edit mode, existing orders start locked
  const [isEditMode, setIsEditMode] = useState(!id);
  // Snapshot of last-saved state, used when the user cancels edits
  const savedSnapshot = useRef(null);

  const [orderNumber, setOrderNumber] = useState('New');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('draft');

  const [customer, setCustomer] = useState(emptyCustomer());
  const [orderDate, setOrderDate] = useState(today());
  const [paymentType, setPaymentType] = useState('cash');
  const [dueDate, setDueDate] = useState('');
  // Credit info for the selected business (null = no credit / not a business)
  const [businessCredit, setBusinessCredit] = useState(null);
  // Eligibility card — for businesses with NO credit limit assigned
  
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [sameAsCompany, setSameAsCompany] = useState(true);
  const [deliveryAddress, setDeliveryAddress] = useState(emptyAddress());
  const [showDeliveryPopup, setShowDeliveryPopup] = useState(false);
  const [notes, setNotes] = useState('');
const [isAgainstPO, setIsAgainstPO] = useState(false);
const [poNumber, setPoNumber]       = useState('');
const [poDate, setPoDate]           = useState('');
  const [paymentTerm, setPaymentTerm] = useState(null); // active payment term from settings
  const [lines, setLines] = useState([emptyLine()]);
  const [amountPaid, setAmountPaid] = useState(0); // total received so far — from ClientReceipts
  const [paymentEntriesList, setPaymentEntriesList] = useState([]);

  useEffect(() => {
  // Always fetch the active payment term (needed for advance calculation)
  API.get('/payment-terms')
    .then(({ data }) => { if (data.terms && data.terms.length) setPaymentTerm(data.terms[0]); })
    .catch(() => {});

  if (!isEdit) {
    API.get('/sales-orders/next-number').then(({ data }) => setOrderNumber(data.orderNumber)).catch(() => {});
    return;
  }
  (async () => {
    try {
      const { data } = await API.get(`/sales-orders/${id}`);
        const o = data.order;
        setOrderNumber(o.orderNumber);
        setStatus(o.status);
        setCustomer({
          businessId: o.businessId, userId: o.userId,
          customerName: o.customerName, customerPhone: o.customerPhone, customerEmail: o.customerEmail || '',
          companyName: o.companyName || '', gstNo: o.gstNo || '', companyAddress: o.companyAddress || '',
        });
        setOrderDate(o.orderDate);
        setPaymentType(o.paymentType);
        setDueDate(o.dueDate || '');
        setDeliveryDate(o.deliveryDate || '');
        setDeliveryType(o.deliveryType);
        setSameAsCompany(!!o.deliverySameAsCompany);
        setDeliveryAddress(parseDeliveryAddress(o.deliveryAddress));
        setNotes(o.notes || '');
        setIsAgainstPO(!!o.isAgainstPO);
        setPoNumber(o.poNumber || '');
        setPoDate(o.poDate || '');
        setLines(o.items && o.items.length ? o.items : [emptyLine()]);

        // Amount already received — recomputed from ClientReceipts via paymentEntries
        const entries = Array.isArray(o.paymentEntries) ? o.paymentEntries : [];
        const paid = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setAmountPaid(paid);
        setPaymentEntriesList(entries);

        savedSnapshot.current = {
          customer: {
            businessId: o.businessId, userId: o.userId,
            customerName: o.customerName, customerPhone: o.customerPhone, customerEmail: o.customerEmail || '',
            companyName: o.companyName || '', gstNo: o.gstNo || '', companyAddress: o.companyAddress || '',
          },
          orderDate: o.orderDate,
          paymentType: o.paymentType,
          dueDate: o.dueDate || '',
          deliveryDate: o.deliveryDate || '',
          deliveryType: o.deliveryType,
          sameAsCompany: !!o.deliverySameAsCompany,
          deliveryAddress: parseDeliveryAddress(o.deliveryAddress),
          notes: o.notes || '',
          isAgainstPO: !!o.isAgainstPO,
          poNumber: o.poNumber || '',
          poDate: o.poDate || '',
          lines: o.items && o.items.length ? o.items : [emptyLine()],
          status: o.status,
          amountPaid: paid,
        };
      } catch {
        toast.error('Could not load order');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const totals = useMemo(() => computeTotals(lines), [lines]);
  const roundedGrandTotal = useMemo(() => Math.round(totals.grandTotal), [totals.grandTotal]);
  const roundOff = useMemo(() => parseFloat((roundedGrandTotal - totals.grandTotal).toFixed(2)), [totals.grandTotal, roundedGrandTotal]);

  const advanceRequired = useMemo(() => {
  if (!paymentTerm) return null;
  const { threshold, minAmount, flatAmount } = paymentTerm;
  if (!threshold && threshold !== 0) return null;

  // If order total ≤ minAmount → 100% advance required
  if (minAmount && roundedGrandTotal <= minAmount) return roundedGrandTotal;

  // Calculate percentage-based advance
  let advance = (roundedGrandTotal * threshold) / 100;

  // If flatAmount is set, take whichever is higher
  if (flatAmount) advance = Math.max(advance, flatAmount);

  return Math.ceil(advance);
}, [paymentTerm, roundedGrandTotal]);

  // Called by CustomerBlock when a business with credit is selected
  const handleCreditDetected = (creditInfo, monthlyBilling = false, monthlyBillingInfo = null) => {
  if (monthlyBilling) {
    setPaymentType('credit');
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    if (monthlyBillingInfo?.paymentDueDays) {
      lastDay.setDate(lastDay.getDate() + monthlyBillingInfo.paymentDueDays);
    }
    setDueDate(lastDay.toISOString().slice(0, 10));
    return;
  }
    if (!creditInfo || creditInfo.creditLimit <= 0) {
      setBusinessCredit(null);
      return;
    }
    setBusinessCredit(creditInfo);
    // Auto-select credit payment type
    setPaymentType('credit');
    // Auto-set due date from tenure
    if (creditInfo.creditTenureDays) {
      const due = new Date();
      due.setDate(due.getDate() + creditInfo.creditTenureDays);
      setDueDate(due.toISOString().slice(0, 10));
    }
  };

  

  // When payment type toggles to credit manually (not via handleCreditDetected)
  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
   
  };

  const handleSave = async () => {
    if (!customer.customerName.trim() || !customer.customerPhone.trim()) {
      toast.error('Customer name and phone are required'); return;
    }
    if (!lines.length || lines.some(l => !l.product.trim())) {
      toast.error('Add at least one product line'); return;
    }
    if (paymentType === 'credit' && !dueDate) {
      toast.error('Due date is required for credit orders'); return;
    }
    if (!deliveryDate) {
      toast.error('Expected delivery date is required'); return;
    }
    if (deliveryType === 'delivery' && !sameAsCompany && !deliveryAddress.line1.trim()) {
      toast.error('Delivery address is required'); return;
    }

    const payload = {
      ...customer,
      orderDate,
      paymentType,
      dueDate: paymentType === 'credit' ? dueDate : null,
      deliveryDate: deliveryDate || null,
      deliveryType,
      deliverySameAsCompany: sameAsCompany,
      // Send as JSON string — backend stores in TEXT column
      deliveryAddress: deliveryType === 'delivery' && !sameAsCompany
        ? JSON.stringify(deliveryAddress)
        : null,
      items: lines,
      status,
      notes,
      isAgainstPO,
      poNumber: isAgainstPO ? poNumber : null,
      poDate:   isAgainstPO ? poDate   : null,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await API.put(`/sales-orders/${id}`, payload);
        toast.success('Order updated');
        await log('updated_order', `Updated sales order #${orderNumber}`, 'SalesOrder', Number(id));
        // Update snapshot to the newly saved state and return to view mode
        savedSnapshot.current = {
          customer: { ...customer },
          orderDate, paymentType, dueDate, deliveryDate, deliveryType,
          sameAsCompany, deliveryAddress: { ...deliveryAddress },
          notes, lines: [...lines], status,
          amountPaid,
          isAgainstPO, poNumber, poDate,
        };
        setIsEditMode(false);
      } else {
        const { data } = await API.post('/sales-orders', payload);
        toast.success(`Order ${data.order.orderNumber} created`);
        await log('created_order', `Created sales order #${data.order.orderNumber}`, 'SalesOrder', data.order.id);
        setIsEditMode(false);
        navigate(`/admin/sales-orders/${data.order.id}`);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const s = savedSnapshot.current;
    if (!s) return;
    setCustomer(s.customer);
    setOrderDate(s.orderDate);
    setPaymentType(s.paymentType);
    setDueDate(s.dueDate);
    setDeliveryDate(s.deliveryDate);
    setDeliveryType(s.deliveryType);
    setSameAsCompany(s.sameAsCompany);
    setDeliveryAddress(s.deliveryAddress);
    setNotes(s.notes);
    setLines(s.lines);
    setStatus(s.status);
    setAmountPaid(s.amountPaid ?? 0);
    setIsAgainstPO(!!s.isAgainstPO);
    setPoNumber(s.poNumber || '');
    setPoDate(s.poDate || '');
    setIsEditMode(false);
  };

  const [pdfGenerating, setPdfGenerating] = useState(false);

  const handleDownloadPDF = async () => {
    try {
      setPdfGenerating(true);
      const orderData = {
        orderNumber,
        orderDate,
        ...customer,
        paymentType,
        dueDate,
        deliveryDate,
        deliveryType,
        status,
        notes,
        isAgainstPO,
        poNumber,
        poDate,
        items: lines,
        taxableAmount:  totals.taxableAmount,
        taxAmount:      totals.taxAmount,
        grandTotal:     roundedGrandTotal,
        amountReceived: amountPaid,
        paymentEntries: paymentEntriesList,
      };
      const pdfBytes = await generateSalesOrderPDF(orderData);
      downloadSalesOrderPDF(pdfBytes, orderNumber);
    } catch (err) {
      toast.error('Failed to generate PDF');
      console.error(err);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this order? This cannot be undone.')) return;
    try {
      await API.delete(`/sales-orders/${id}`);
      toast.success('Order deleted');
      navigate('/admin/sales-orders');
    } catch {
      toast.error('Failed to delete order');
    }
  };
  // ── Confirm Order ─────────────────────────────────────────────────
  const handleConfirm = async () => {
    setStatus('confirmed');
    // We need status to be 'confirmed' before handleSave reads it from state.
    // Since setState is async, we call the API directly here with confirmed status.
    if (!customer.customerName.trim() || !customer.customerPhone.trim()) {
      toast.error('Customer name and phone are required'); return;
    }
    if (!lines.length || lines.some(l => !l.product.trim())) {
      toast.error('Add at least one product line'); return;
    }
    try {
      setSaving(true);
      await API.put(`/sales-orders/${id}`, { status: 'confirmed' });
      setStatus('confirmed');
      toast.success('Order confirmed');
      await log('confirmed_order', `Confirmed sales order #${orderNumber}`, 'SalesOrder', Number(id));
      savedSnapshot.current = { ...savedSnapshot.current, status: 'confirmed' };
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm order');
      setStatus('draft');
    } finally {
      setSaving(false);
    }
  };

  // ── Record Payment (ClientReceipt-based) ──────────────────────────
  // Replaces the old handleLogPayment that wrote directly to SalesOrder.paymentEntries.
  // Now creates a ClientReceipt so payments are visible in the ledger.
  const [showPayModal, setShowPayModal]   = useState(false);
  const [payForm, setPayForm]             = useState({ mode: 'cash', bankAccountId: '', amount: '', date: today(), note: '' });
  const [nextReceiptNo, setNextReceiptNo] = useState('');
  const [bankAccounts, setBankAccounts]   = useState([]);
  const setPay = (field, val) => setPayForm(f => ({ ...f, [field]: val }));

  // Load bank accounts once
  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  // Fetch next receipt number when modal opens
  useEffect(() => {
    if (!showPayModal) return;
    API.get('/client-receipts/next-number', { params: { type: 'order' } })
      .then(({ data }) => setNextReceiptNo(data.receiptNumber || ''))
      .catch(() => {});
  }, [showPayModal]);

  const handleRecordPayment = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }

    // Re-fetch live order balance to prevent over-payment
    const { data: live } = await API.get(`/sales-orders/${id}`);
    const liveBalance = Number(live.order?.grandTotal || 0) - Number(live.order?.amountReceived || 0);
    if (amt > liveBalance + 0.01) {
      toast.error(`Amount exceeds balance (max ₹${liveBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })})`);
      return;
    }

    try {
      const payload = {
        salesOrderId:     id,
        businessId:       customer.businessId || null,
        customerName:     customer.customerName || customer.companyName || '',
        customerPhone:    customer.customerPhone || '',
        companyName:      customer.companyName || '',
        totalAmount:      amt,
        creditUsed:       0,
        receiptDate:      payForm.date,
        mode:             payForm.mode,
        bankAccountId:    payForm.bankAccountId || null,
        bankAccountLabel: bankAccounts.find(a => String(a.id) === String(payForm.bankAccountId))?.account_name || null,
        note:             payForm.note,
        billAllocations:  [], // order-linked — no bill allocations
      };

      await API.post('/client-receipts', payload);
      toast.success(`Payment recorded — Receipt ${nextReceiptNo}`);
      setShowPayModal(false);
      setPayForm({ mode: 'cash', bankAccountId: '', amount: '', date: today(), note: '' });
      // Reload order to show updated amountReceived
      const { data: updated } = await API.get(`/sales-orders/${id}`);
      if (updated.order) {
        setAmountPaid(Number(updated.order.amountReceived || 0));
        setPaymentEntriesList(Array.isArray(updated.order.paymentEntries) ? updated.order.paymentEntries : []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    }
  };




  const hasDeliveryAddress = deliveryAddress.line1.trim() !== '';

  // Opens the delivery address popup. Used both when the customer first
  // switches to Home Delivery and to edit an already-set address afterwards.
  const openDeliveryPopup = () => setShowDeliveryPopup(true);

  if (loading) return <div className="so-page"><div className="so-empty">Loading…</div></div>;

  return (
    <div className="so-page">
      <div className="so-page-head">
        <Link to="/admin/sales-orders" className="so-back-link"><FiArrowLeft size={16} /> Back to Orders</Link>
        <div className="so-page-head-actions">
          {businessCredit && businessCredit.availableCredit > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.2)',
              borderRadius: 8,
              fontSize: '0.82rem',
              fontWeight: 700,
              color: '#7c3aed',
              whiteSpace: 'nowrap',
            }}>
              💳 Credit Left: ₹{Number(businessCredit.availableCredit).toLocaleString('en-IN')}
            </div>
          )}
          {advanceRequired !== null && advanceRequired > 0 && paymentType === 'cash' && (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 8,
    fontSize: '0.82rem',
    fontWeight: 700,
    color: '#92400e',
    whiteSpace: 'nowrap',
  }}>
    ⚡ Advance: {money(advanceRequired)}
  </div>
)}

          {isEdit && (
            <button
              className="so-btn"
              onClick={handleDownloadPDF}
              disabled={pdfGenerating}
              title="Download Sales Order PDF"
            >
              <FiDownload size={15} /> {pdfGenerating ? 'Generating…' : 'Download PDF'}
            </button>
          )}
          {isEdit && !isEditMode && (
            <button className="so-btn so-btn--edit" onClick={() => setIsEditMode(true)}>
              <FiEdit2 size={15} /> Edit
            </button>
          )}
          {isEdit && !isEditMode && (
            <button className="so-btn so-btn--primary" onClick={() => navigate('/admin/sales-orders/new')}>
              <FiPlus size={15} /> New Order
            </button>
          )}
          {isEdit && isEditMode && (
            <button className="so-btn so-btn--cancel" onClick={handleCancel}>
              <FiX size={15} /> Cancel
            </button>
          )}
          {isEdit && !isEditMode && status === 'draft' && (
            <button className="so-btn so-btn--confirm" onClick={handleConfirm} disabled={saving}>
              <FiCheck size={15} /> {saving ? 'Confirming…' : 'Confirm Order'}
            </button>
          )}
          {isEdit && (
            <button className="so-btn so-btn--success" onClick={() => setShowPayModal(true)}>
              <FiDollarSign size={15} /> Record Payment
            </button>
          )}
          {(!isEdit || isEditMode) && (
            <button
              className="so-btn so-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              <FiSave size={15} /> {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Order'}
            </button>
          )}
        </div>
      </div>

      <div className="so-doc">
        {/* Header */}
        <div className="so-doc-header">
          <div>
            <div className="so-doc-title">Order</div>
            <div className="so-doc-number">{orderNumber}</div>
          </div>
          {isEdit && isEditMode && (
            <select className="so-status-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="draft">Draft</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          )}
          {isEdit && !isEditMode && (
            <span className={`so-badge so-badge--${STATUS_META[status]?.tone || 'gray'}`} style={{ fontSize: '0.85rem', padding: '6px 14px', borderRadius: 8 }}>
              {STATUS_META[status]?.label || status}
            </span>
          )}
        </div>

        {/* Top grid: customer (left) / order meta (right) */}
        <div className="so-doc-toprow">

          {/* LEFT COLUMN — Customer + Delivery Address card */}
          <div className="so-doc-col">
            <CustomerBlock
              customer={customer}
              locked={isEdit && !isEditMode}
              setCustomer={(c) => {
                setCustomer(c);
                // If customer cleared, reset credit state
                if (!c.customerName) {
                  setBusinessCredit(null);
                  setPaymentType('cash');
                  setDueDate('');                  
                }
              }}
              onCreditDetected={handleCreditDetected}
            />

            {/* Delivery address card — shown below customer box after saving */}
            {deliveryType === 'delivery' && hasDeliveryAddress && (
              <div className="so-customer-card" style={{ marginTop: 10 }}>
                <div className="so-customer-card-head">
                  <span className="so-field-label"><FiMapPin size={12} /> Delivery Address</span>
                  <button
                    className="so-link-btn"
                    onClick={openDeliveryPopup}
                  >
                    ✏️ Edit
                  </button>
                </div>
                <div className="so-customer-addr">{deliveryAddress.line1}</div>
                {deliveryAddress.line2 && <div className="so-customer-addr">{deliveryAddress.line2}</div>}
                {(deliveryAddress.city || deliveryAddress.state || deliveryAddress.pincode) && (
                  <div className="so-customer-addr">
                    {[deliveryAddress.city, deliveryAddress.state, deliveryAddress.pincode].filter(Boolean).join(', ')}
                  </div>
                )}
                {deliveryAddress.country && <div className="so-customer-addr">{deliveryAddress.country}</div>}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN — Order meta */}
          <div className="so-doc-col so-doc-col--meta">
            <div className="so-meta-row">
              <label className="so-field-label"><FiCalendar size={12} /> Order Date</label>
              {isEdit && !isEditMode ? (
                <span className="so-view-value">{orderDate}</span>
              ) : (
                <input
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                />
              )}
            </div>

            <div className="so-meta-row">
              <label className="so-field-label">Payment</label>
              {isEdit && !isEditMode ? (
                <span className="so-view-value" style={{ textTransform: 'capitalize' }}>{paymentType}</span>
              ) : (
                <div className="so-toggle">
                  <button className={`so-toggle-btn ${paymentType === 'cash' ? 'so-toggle-btn--active' : ''}`} onClick={() => handlePaymentTypeChange('cash')}>Cash</button>
                  <button className={`so-toggle-btn ${paymentType === 'credit' ? 'so-toggle-btn--active' : ''}`} onClick={() => handlePaymentTypeChange('credit')}>Credit</button>
                </div>
              )}
            </div>



            


            {paymentType === 'credit' && (
              <div className="so-meta-row">
                <label className="so-field-label">Due Date</label>
                {isEdit && !isEditMode ? (
                  <span className="so-view-value">{dueDate || '—'}</span>
                ) : (
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  />
                )}
              </div>
            )}

            <div className="so-meta-row">
              <label className="so-field-label"><FiCalendar size={12} /> Expected Delivery Date <span style={{ color: 'red' }}>*</span></label>
              {isEdit && !isEditMode ? (
                <span className="so-view-value">{deliveryDate || '—'}</span>
              ) : (
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  required
                />
              )}
            </div>

            {/* Purchase Order reference */}
            <div className="so-meta-row">
              <label className="so-field-label">Purchase Order</label>
              {isEdit && !isEditMode ? (
                <span className="so-view-value">
                  {isAgainstPO ? (poNumber || '—') : <span style={{ color: '#9ca3af' }}>No</span>}
                </span>
              ) : (
                <label className="so-checkbox-row" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={isAgainstPO}
                    onChange={(e) => {
                      setIsAgainstPO(e.target.checked);
                      if (!e.target.checked) { setPoNumber(''); setPoDate(''); }
                    }}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                  />
                  Against a PO?
                </label>
              )}
            </div>

            {isAgainstPO && (
              <>
                <div className="so-meta-row">
                  <label className="so-field-label">PO Number</label>
                  {isEdit && !isEditMode ? (
                    <span className="so-view-value">{poNumber || '—'}</span>
                  ) : (
                    <input
                      type="text"
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      placeholder="e.g. PO-2026-0042"
                    />
                  )}
                </div>
                <div className="so-meta-row">
                  <label className="so-field-label"><FiCalendar size={12} /> PO Date</label>
                  {isEdit && !isEditMode ? (
                    <span className="so-view-value">{poDate || '—'}</span>
                  ) : (
                    <input
                      type="date"
                      value={poDate}
                      onChange={(e) => setPoDate(e.target.value)}
                      onKeyDown={(e) => e.preventDefault()}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                  )}
                </div>
              </>
            )}

            <div className="so-meta-row">
              <label className="so-field-label"><FiMapPin size={12} /> Delivery</label>
              {isEdit && !isEditMode ? (
                <span className="so-view-value">{deliveryType === 'delivery' ? 'Home Delivery' : 'Store Pickup'}</span>
              ) : (
                <div className="so-toggle">
                  <button className={`so-toggle-btn ${deliveryType === 'pickup' ? 'so-toggle-btn--active' : ''}`} onClick={() => setDeliveryType('pickup')}>Store Pickup</button>
                  <button
                    className={`so-toggle-btn ${deliveryType === 'delivery' ? 'so-toggle-btn--active' : ''}`}
                    onClick={() => { setDeliveryType('delivery'); openDeliveryPopup(); }}
                  >
                    Home Delivery
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Line items */}
        <LineTable lines={lines} setLines={isEdit && !isEditMode ? () => {} : setLines} readOnly={isEdit && !isEditMode} />

        {/* Notes + totals */}
<div className="so-doc-footer">
  <div className="so-notes-col">
    <label className="so-field-label">Notes</label>
    {isEdit && !isEditMode ? (
      <div className="so-view-value" style={{ minHeight: 60, padding: '8px 0', whiteSpace: 'pre-wrap', color: notes ? '#374151' : '#9ca3af' }}>
        {notes || 'No notes'}
      </div>
    ) : (
      <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Terms and conditions / internal notes" />
    )}

    
  </div>
          <div className="so-totals-col">
            <div className="so-totals-row"><span>Taxable Amount</span><span>{money(totals.taxableAmount)}</span></div>
            <div className="so-totals-row"><span>Tax Amount</span><span>{money(totals.taxAmount)}</span></div>
            {roundOff !== 0 && (
              <div className="so-totals-row" style={{ opacity: 0.65, fontSize: '0.92em' }}><span>Round Off</span><span>{roundOff > 0 ? '+' : '-'}{money(Math.abs(roundOff))}</span></div>
            )}
            <div className="so-totals-row so-totals-row--grand"><span>Grand Total</span><span>{money(roundedGrandTotal)}</span></div>
            {isEdit && amountPaid > 0 && (
              <div className="so-totals-row" style={{ color:'#16a34a' }}><span>Amount Paid</span><span>- {money(amountPaid)}</span></div>
            )}
            {isEdit && amountPaid > 0 && (
              <div className="so-totals-row so-totals-row--grand" style={{ color: roundedGrandTotal - amountPaid <= 0 ? '#16a34a' : '#dc2626' }}>
                <span>Balance Due</span>
                <span>{money(Math.max(0, roundedGrandTotal - amountPaid))}</span>
              </div>
)}
          </div>
        </div>
      </div>

      {/* Delivery Address Popup — rendered at root level to avoid stacking context issues */}
      {showDeliveryPopup && (
        <DeliveryAddressPopup
          initial={deliveryAddress}
          initialSameAsCompany={sameAsCompany}
          companyAddress={customer.companyAddress}
          onSave={({ address, sameAsCompany: same }) => {
            setDeliveryAddress(address);
            setSameAsCompany(same);
            setShowDeliveryPopup(false);
          }}
          onClose={() => setShowDeliveryPopup(false)}
        />
      )}
      {/* Record Payment Modal — creates a ClientReceipt against this order */}
      {showPayModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:440, maxWidth:'95vw', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:700 }}>Record Payment</h3>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:6 }}>{orderNumber}</div>
            {nextReceiptNo && (
              <div style={{ fontSize:12, color:'#6b7280', marginBottom:14, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6, padding:'5px 10px', display:'inline-block' }}>
                Receipt No: <strong style={{ color:'#16a34a' }}>{nextReceiptNo}</strong> <span style={{ opacity:0.7 }}>(auto-assigned)</span>
              </div>
            )}
            <div style={{ display:'flex', gap:16, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'8px 12px', marginBottom:16, fontSize:13 }}>
              <span style={{ color:'#15803d' }}>Received: <strong>{money(amountPaid)}</strong></span>
              <span style={{ color: roundedGrandTotal - amountPaid <= 0 ? '#15803d' : '#dc2626' }}>Balance: <strong>{money(Math.max(0, roundedGrandTotal - amountPaid))}</strong></span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:4 }}>Amount (₹) *</label>
                <input type="number" min="0" value={payForm.amount} onChange={e => setPay('amount', e.target.value)} placeholder="0" style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:4 }}>Date</label>
                <input type="date" value={payForm.date} onChange={e => setPay('date', e.target.value)} onClick={e => e.currentTarget.showPicker?.()} style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none', cursor:'pointer' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:4 }}>Mode</label>
                <select value={payForm.mode} onChange={e => { setPay('mode', e.target.value); setPay('bankAccountId', ''); }} style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none' }}>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:4 }}>Note</label>
                <input type="text" value={payForm.note} onChange={e => setPay('note', e.target.value)} placeholder="Optional" style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none' }} />
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:4 }}>Received In Account</label>
                <select value={payForm.bankAccountId} onChange={e => setPay('bankAccountId', e.target.value)} style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none' }}>
                  <option value="">— Select Account —</option>
                  {bankAccounts
                    .filter(a => {
                      if (payForm.mode === 'cash')         return a.account_type === 'cash';
                      if (payForm.mode === 'upi')          return a.account_type === 'upi';
                      if (payForm.mode === 'bank_transfer' || payForm.mode === 'cheque') return a.account_type === 'bank';
                      return true;
                    })
                    .map(a => (
                      <option key={a.id} value={a.id}>
                        {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button onClick={() => setShowPayModal(false)} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #e5e7eb', background:'#f9fafb', fontWeight:600, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleRecordPayment} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'var(--primary)', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' }}>Save Payment</button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Root export — mounted at /admin/sales-orders/*
   ──────────────────────────────────────────────────────────────────── */

const SalesOrders = () => {
  const { id } = useParams();
  return (
    <Routes>
      <Route index element={<OrderListPage />} />
      <Route path="new" element={<OrderFormPage key="new" />} />
      <Route path=":id" element={<OrderFormPage key={id} />} />
    </Routes>
  );
};

export default SalesOrders;